import React, { useState, useCallback } from 'react';
import { ArrowLeft, Settings, CheckCircle } from 'lucide-react';

// ── Global App Settings key (shared by all Finance vouchers) ──
const SETTINGS_KEY = 'erp_app_settings';

export const readAppSettings = () => {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); }
  catch { return {}; }
};

const SECTIONS = [
  {
    id: 'transactions',
    label: 'Transaction Validation',
    settings: [
      {
        key: 'billRefMandatory',
        label: 'Bill Reference',
        description: 'When Against = "Against Bill" — require Bill Reference before adding a line entry',
        options: [
          { value: false, label: 'Optional', hint: 'Bill Ref can be left empty' },
          { value: true,  label: 'Mandatory', hint: 'Line cannot be added without Bill Ref' },
        ],
      },
    ],
  },
  {
    id: 'journal',
    label: 'Journal Entry Rules',
    settings: [
      {
        key: 'jvRequireNarration',
        label: 'Narration / Remarks',
        description: 'Require at least one line to have a Remarks entry in Journal Vouchers',
        options: [
          { value: false, label: 'Optional', hint: 'Remarks can be left empty' },
          { value: true,  label: 'Mandatory', hint: 'At least one line must have Remarks' },
        ],
      },
    ],
  },
];

const FinanceSettingsScreen = ({ theme = 'light', navigateTo }) => {
  const [settings, setSettings] = useState(readAppSettings);
  const [saved, setSaved] = useState(false);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
      return next;
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, []);

  return (
    <div className="erp-module" style={{ flexDirection: 'column', minHeight: 0 }}>
      {/* ── Header ── */}
      <div className="erp-screen-header" data-testid="finance-settings-screen">
        {navigateTo && (
          <button className="erp-btn erp-btn-ghost" onClick={() => navigateTo('HUB')} data-testid="settings-back-btn" style={{ marginRight: 8 }}>
            <ArrowLeft size={13} /> Back
          </button>
        )}
        <Settings size={15} style={{ marginRight: 6, color: 'var(--erp-primary)' }} />
        <span className="erp-screen-title">Finance Application Settings</span>
        <div className="erp-screen-actions">
          {saved && (
            <span style={{ fontSize: 12, color: 'var(--erp-success)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={13} /> Saved
            </span>
          )}
        </div>
      </div>

      {/* ── Settings Body ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ fontSize: 12, color: 'var(--erp-text-muted)', margin: 0 }}>
          These settings apply globally across all Finance vouchers and modules. Changes take effect immediately.
        </p>

        {SECTIONS.map(section => (
          <div key={section.id} className="erp-form-card" style={{ padding: '14px 18px' }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 12, color: 'var(--erp-text)', borderBottom: '1px solid var(--erp-border)', paddingBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {section.label}
            </div>
            {section.settings.map(s => {
              const current = settings[s.key] ?? false;
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, padding: '8px 0', borderBottom: '1px solid var(--erp-border)' }} data-testid={`setting-row-${s.key}`}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--erp-text)', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--erp-text-muted)' }}>{s.description}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, paddingTop: 2 }}>
                    {s.options.map(opt => (
                      <button
                        key={String(opt.value)}
                        data-testid={`setting-${s.key}-${opt.value}`}
                        onClick={() => updateSetting(s.key, opt.value)}
                        title={opt.hint}
                        style={{
                          padding: '4px 14px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          border: `1.5px solid ${current === opt.value ? 'var(--erp-primary)' : 'var(--erp-border)'}`,
                          background: current === opt.value ? 'var(--erp-primary)' : 'transparent',
                          color: current === opt.value ? '#fff' : 'var(--erp-text-muted)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ fontSize: 11, color: 'var(--erp-text-muted)' }}>
          Settings are stored in browser localStorage and are specific to this browser session.
        </div>
      </div>
    </div>
  );
};

export default FinanceSettingsScreen;
